<!--testing only not going to be used-->

<script setup>
import { ref, computed, onMounted } from 'vue'
import { page2, seedFromGarden03, addBlock, removeBlock, moveBlock } from '@/builder2/builder2Store'
import {
  blockRegistry,
  blockOrder,
  getBlockVariants,
  getBlockComponent,
} from '@/builder2/blockRegistry'
import FieldRenderer from '@/components/builder/FieldRenderer.vue'
import ImagePickerModal from '@/components/builder/ImagePickerModal.vue'
import { company, getAuthToken, API_BASE_URL } from '@/stores/appConfig'

const selectedBlockId = ref(null)

onMounted(() => {
  if (!page2.blocks.length) seedFromGarden03()
  if (!selectedBlockId.value && page2.blocks.length) {
    selectedBlockId.value = page2.blocks[0].id
  }
})

const selectedBlock = computed(() =>
  page2.blocks.find(b => b.id === selectedBlockId.value) ?? null
)

const selectedIndex = computed(() =>
  page2.blocks.findIndex(b => b.id === selectedBlockId.value)
)

// FieldRenderer expects content[sectionKey][fieldKey]; blocks store content flat,
// so this wraps the selected block's live content under its own id as the key.
const fieldRendererContent = computed(() => {
  if (!selectedBlock.value) return {}
  return { [selectedBlock.value.id]: selectedBlock.value.content }
})

function selectBlock(id) {
  selectedBlockId.value = id
}

function handleAddBlock(type) {
  const added = addBlock(type, selectedIndex.value)
  if (added) selectedBlockId.value = added.id
}

function handleRemoveBlock(id) {
  const wasSelected = id === selectedBlockId.value
  const idx = page2.blocks.findIndex(b => b.id === id)
  removeBlock(id)
  if (wasSelected) {
    const fallback = page2.blocks[idx] ?? page2.blocks[idx - 1] ?? null
    selectedBlockId.value = fallback?.id ?? null
  }
}

function handleReset() {
  seedFromGarden03()
  selectedBlockId.value = page2.blocks[0]?.id ?? null
}

// ── Image picker (read-only library select; no upload/delete wired) ───────
const imagePickerOpen = ref(false)
const imagePickerFieldTarget = ref(null)
const imagePickerSelectedUrl = ref('')
const tenantImages = ref([])
const tenantImagesLoading = ref(false)

async function loadTenantImages() {
  if (!company.company_id) return
  tenantImagesLoading.value = true
  try {
    const token = await getAuthToken()
    const response = await fetch(`${API_BASE_URL}/media?company_id=${company.company_id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    const data = await response.json().catch(() => ({}))
    if (response.ok) tenantImages.value = data.images || data.media || []
  } catch (error) {
    console.error('loadTenantImages error:', error)
  } finally {
    tenantImagesLoading.value = false
  }
}

function handleImagePickFromField({ sectionKey, fieldKey }) {
  imagePickerFieldTarget.value = { sectionKey, fieldKey }
  imagePickerSelectedUrl.value = fieldRendererContent.value[sectionKey]?.[fieldKey]?.value || ''
  imagePickerOpen.value = true
  loadTenantImages()
}

function handleImageSelect(image) {
  if (!image?.url || !imagePickerFieldTarget.value) return
  const { sectionKey, fieldKey } = imagePickerFieldTarget.value
  const target = fieldRendererContent.value[sectionKey]
  if (target?.[fieldKey]) target[fieldKey].value = image.url
  imagePickerOpen.value = false
}
</script>

<template>
  <div class="b2-shell">
    <!-- Palette -->
    <aside class="b2-palette">
      <div class="b2-panel-title">Add block</div>
      <button
        v-for="type in blockOrder"
        :key="type"
        type="button"
        class="b2-palette-item"
        @click="handleAddBlock(type)"
      >
        <v-icon size="18">{{ blockRegistry[type].icon }}</v-icon>
        <span>{{ blockRegistry[type].label }}</span>
        <v-icon size="16" class="b2-palette-add">mdi-plus</v-icon>
      </button>

      <button type="button" class="b2-reset-btn" @click="handleReset">
        <v-icon size="16">mdi-restore</v-icon>
        Reset to demo page
      </button>
    </aside>

    <!-- Canvas -->
    <main class="b2-canvas">
      <div v-if="!page2.blocks.length" class="b2-empty">
        No blocks yet — add one from the palette.
      </div>

      <div
        v-for="(block, index) in page2.blocks"
        :key="block.id"
        class="b2-block-wrap"
        :class="{ selected: block.id === selectedBlockId }"
        @click="selectBlock(block.id)"
      >
        <div class="b2-block-toolbar">
          <span class="b2-block-label">{{ blockRegistry[block.type].label }}</span>
          <div class="b2-block-actions">
            <button type="button" title="Move up" :disabled="index === 0" @click.stop="moveBlock(block.id, -1)">
              <v-icon size="18">mdi-arrow-up</v-icon>
            </button>
            <button type="button" title="Move down" :disabled="index === page2.blocks.length - 1" @click.stop="moveBlock(block.id, 1)">
              <v-icon size="18">mdi-arrow-down</v-icon>
            </button>
            <button type="button" title="Remove block" @click.stop="handleRemoveBlock(block.id)">
              <v-icon size="18">mdi-trash-can-outline</v-icon>
            </button>
          </div>
        </div>
        <div class="b2-block-frame">
          <component
            :is="getBlockComponent(block.type, block.variant)"
            :content="block.content"
            :variant="block.variant"
          />
        </div>
      </div>
    </main>

    <!-- Properties -->
    <aside class="b2-properties">
      <div v-if="!selectedBlock" class="b2-empty">Select a block to edit its content.</div>
      <template v-else>
        <div class="b2-panel-title">{{ blockRegistry[selectedBlock.type].label }}</div>
        <label
          v-if="getBlockVariants(selectedBlock.type).length > 1"
          class="b2-variation-field"
        >
          <span>Design variation</span>
          <select v-model="selectedBlock.variant">
            <option
              v-for="variant in getBlockVariants(selectedBlock.type)"
              :key="variant.id"
              :value="variant.id"
            >
              {{ variant.label }}
            </option>
          </select>
        </label>
        <div
          v-for="(field, key) in selectedBlock.content"
          :key="key"
          class="b2-field-card"
        >
          <FieldRenderer
            :section-key="selectedBlock.id"
            :field-key="key"
            :field="field"
            :content="fieldRendererContent"
            @image-pick="handleImagePickFromField"
          />
        </div>
      </template>
    </aside>
  </div>

  <ImagePickerModal
    v-model="imagePickerOpen"
    :images="tenantImages"
    :loading="tenantImagesLoading"
    :selected-url="imagePickerSelectedUrl"
    title="Choose image"
    subtitle="Select an image."
    @select="handleImageSelect"
  />
</template>

<style scoped>
.b2-shell {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr) 320px;
  height: 100vh;
  overflow: hidden;
  background: #eef1f5;
}

.b2-palette, .b2-properties {
  overflow-y: auto;
  padding: 16px;
  background: #fff;
  border-right: 1px solid #dbe3ef;
}

.b2-properties {
  border-right: none;
  border-left: 1px solid #dbe3ef;
}

.b2-panel-title {
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #334155;
  margin-bottom: 12px;
}

.b2-variation-field {
  display: grid;
  gap: 7px;
  margin: 6px 0 10px;
  padding: 12px;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  background: #f8fafc;
  color: #46566a;
  font-size: 0.78rem;
  font-weight: 700;
}

.b2-variation-field select {
  width: 100%;
  padding: 9px 10px;
  border: 1px solid #ccd5e1;
  border-radius: 6px;
  background: #fff;
  color: #243247;
  font: inherit;
}

.b2-palette-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  margin-bottom: 6px;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  background: #f8fafc;
  cursor: pointer;
  font-size: 0.85rem;
  color: #334155;
  text-align: left;
  transition: border-color .15s, background .15s;
}

.b2-palette-item:hover { border-color: #4a8c3f; background: #f2f8f0; }
.b2-palette-item span { flex: 1; }
.b2-palette-add { color: #4a8c3f; }

.b2-reset-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  padding: 10px 12px;
  border: 1px dashed #c7d2e0;
  border-radius: 8px;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  font-size: 0.8rem;
}

.b2-canvas {
  overflow-y: auto;
  padding: 24px;
}

.b2-empty {
  padding: 40px 20px;
  text-align: center;
  color: #94a3b8;
  font-size: 0.85rem;
}

.b2-block-wrap {
  position: relative;
  margin-bottom: 18px;
  border: 2px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: border-color .15s;
}

.b2-block-wrap:hover { border-color: #b8d9b2; }
.b2-block-wrap.selected { border-color: #4a8c3f; }

.b2-block-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: #1a3320;
  color: white;
  border-radius: 4px 4px 0 0;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .04em;
}

.b2-block-actions { display: flex; gap: 4px; }
.b2-block-actions button {
  background: rgba(255,255,255,.1);
  border: none;
  color: white;
  border-radius: 4px;
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  cursor: pointer;
}
.b2-block-actions button:hover { background: rgba(255,255,255,.2); }
.b2-block-actions button:disabled { opacity: .35; cursor: not-allowed; }

.b2-block-frame {
  overflow: hidden;
  border-radius: 0 0 4px 4px;
}

.b2-field-card {
  margin-bottom: 8px;
  padding: 12px;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  background: #f8fafc;
  transition: border-color .15s, background .15s;
}

.b2-field-card:hover {
  border-color: #b8d9b2;
  background: #fbfdfb;
}
</style>
